/**
 * Call-snippet generation for the AI Service detail page's Connect tab
 * (task 380).
 *
 * Builds the public service URL plus cURL / JavaScript / Python call snippets
 * and a response example, all derived from the endpoint's stored `input` and
 * `output` JSON schemas so the reference can never go stale. Pure functions —
 * no React, no network.
 *
 * URL composition is delegated to `src/utils/endpointUrl.ts` — the single
 * definition of the `${config.API_URL}/api/{accountSlug}/{path}` shape (task
 * 385). Every snippet here is a call reference a User copies into their own
 * system, so it must carry the owning account's slug or it will not resolve.
 */

import type { Endpoint } from '@/types';
import {
  bareEndpointPath,
  endpointDisplayPath,
  endpointInvokeUrl,
  type EndpointUrlParts,
} from '@/utils/endpointUrl';

const BODY_METHODS = new Set(['POST', 'PUT', 'PATCH']);

/** The endpoint fields the snippet builders read. */
export type SnippetEndpoint = Pick<
  Endpoint,
  'path' | 'method' | 'input' | 'requestFormat'
> & { accountSlug?: string | null };

/**
 * Display form of a service's callable path, with exactly one leading slash and
 * the owning account slug when the row carries one: `/{accountSlug}/{path}`.
 */
export function normalizeServicePath(
  endpointOrPath: EndpointUrlParts | string
): string {
  return typeof endpointOrPath === 'string'
    ? endpointDisplayPath({ path: endpointOrPath })
    : endpointDisplayPath(endpointOrPath);
}

/** The public URL systems call to invoke this AI Service. */
export function serviceUrl(endpoint: EndpointUrlParts): string {
  return endpointInvokeUrl(endpoint);
}

/**
 * Build a realistic example value from a JSON-schema node. Precedence:
 * explicit `example` / `examples` / `default` / first `enum` value, then a
 * type-derived placeholder. Objects and arrays recurse (depth-capped so a
 * self-referential schema cannot loop).
 */
export function exampleFromSchema(
  schema: Record<string, unknown> | null | undefined,
  hint = 'value',
  depth = 0
): unknown {
  if (!schema || depth > 4) return null;

  if (schema.example !== undefined) return schema.example;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) {
    return schema.examples[0];
  }
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const type =
    (schema.type as string | undefined) ?? (schema.properties ? 'object' : 'string');

  switch (type) {
    case 'object': {
      const properties = (schema.properties ?? {}) as Record<
        string,
        Record<string, unknown>
      >;
      const body: Record<string, unknown> = {};
      for (const [name, prop] of Object.entries(properties)) {
        body[name] = exampleFromSchema(prop, name, depth + 1);
      }
      return body;
    }
    case 'array': {
      const items = schema.items as Record<string, unknown> | undefined;
      return [exampleFromSchema(items, hint, depth + 1)];
    }
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'string':
    default: {
      const format = schema.format as string | undefined;
      if (format === 'email') return 'user@example.com';
      if (format === 'uri') return 'https://example.com';
      if (format === 'date') return '2026-01-01';
      if (format === 'date-time') return '2026-01-01T00:00:00Z';
      return `example ${hint}`;
    }
  }
}

/** Example request body derived from the endpoint's input schema. */
export function buildExampleBody(endpoint: Pick<Endpoint, 'input'>): Record<string, unknown> {
  const example = exampleFromSchema(endpoint.input ?? undefined, 'input');
  return example && typeof example === 'object' && !Array.isArray(example)
    ? (example as Record<string, unknown>)
    : {};
}

/** Example response body derived from the endpoint's output schema. */
export function buildResponseExample(endpoint: Pick<Endpoint, 'output'>): string {
  const example = exampleFromSchema(endpoint.output ?? undefined, 'output');
  return JSON.stringify(example ?? {}, null, 2);
}

/** Query string for GET/DELETE, from top-level input schema properties. */
function exampleQueryString(endpoint: Pick<Endpoint, 'input'>): string {
  const body = buildExampleBody(endpoint);
  const entries = Object.entries(body);
  if (entries.length === 0) return '';
  return `?${entries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          typeof value === 'object' ? JSON.stringify(value) : String(value)
        )}`
    )
    .join('&')}`;
}

function isBodyMethod(method: string): boolean {
  return BODY_METHODS.has(method.toUpperCase());
}

/**
 * The address the snippets call (task 857).
 *
 * Defaults to the platform URL. The Developer Info tab passes the account's
 * custom-domain address instead when it has one serving, because a snippet is
 * the thing a developer pastes into their own system — it must carry the same
 * address the surface above it leads with, or the copy button hands over a URL
 * the page is not showing. The shape itself is never composed here: the caller
 * gets it from `utils/endpointUrl`.
 */
export interface SnippetOptions {
  /** Overrides the platform URL. */
  url?: string;
}

const snippetUrl = (endpoint: SnippetEndpoint, options?: SnippetOptions): string =>
  options?.url ?? serviceUrl(endpoint);

/** cURL call snippet — always authenticates with the x-api-key account key. */
export function buildCurlSnippet(
  endpoint: SnippetEndpoint,
  options?: SnippetOptions
): string {
  const url = snippetUrl(endpoint, options);
  const mediaType = endpoint.requestFormat || 'application/json';

  if (!isBodyMethod(endpoint.method)) {
    return [
      `curl -X ${endpoint.method} "${url}${exampleQueryString(endpoint)}"`,
      `  -H "x-api-key: $INNOVATE_API_KEY"`,
    ].join(' \\\n');
  }

  const body = JSON.stringify(buildExampleBody(endpoint), null, 2);
  return [
    `curl -X ${endpoint.method} ${url}`,
    `  -H "Content-Type: ${mediaType}"`,
    `  -H "x-api-key: $INNOVATE_API_KEY"`,
    `  -d '${body}'`,
  ].join(' \\\n');
}

/** JavaScript (fetch) call snippet. */
export function buildJavaScriptSnippet(
  endpoint: SnippetEndpoint,
  options?: SnippetOptions
): string {
  const mediaType = endpoint.requestFormat || 'application/json';

  if (!isBodyMethod(endpoint.method)) {
    const url = `${snippetUrl(endpoint, options)}${exampleQueryString(endpoint)}`;
    return `const response = await fetch(${JSON.stringify(url)}, {
  method: ${JSON.stringify(endpoint.method)},
  headers: { "x-api-key": process.env.INNOVATE_API_KEY },
});

const result = await response.json();`;
  }

  const body = JSON.stringify(buildExampleBody(endpoint), null, 2)
    .split('\n')
    .map((line, index) => (index === 0 ? line : `  ${line}`))
    .join('\n');

  return `const response = await fetch(${JSON.stringify(snippetUrl(endpoint, options))}, {
  method: ${JSON.stringify(endpoint.method)},
  headers: {
    "Content-Type": ${JSON.stringify(mediaType)},
    "x-api-key": process.env.INNOVATE_API_KEY,
  },
  body: JSON.stringify(${body}),
});

const result = await response.json();`;
}

/**
 * Serialize a value as a Python literal (True/False/None instead of the JSON
 * spellings) so the requests snippet is valid Python, not pasted JSON.
 */
function pyLiteral(value: unknown, indent = 0): string {
  const pad = '    '.repeat(indent);
  const childPad = '    '.repeat(indent + 1);
  if (value === null || value === undefined) return 'None';
  if (value === true) return 'True';
  if (value === false) return 'False';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map(item => `${childPad}${pyLiteral(item, indent + 1)}`);
    return `[\n${items.join(',\n')},\n${pad}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return '{}';
  const items = entries.map(
    ([key, val]) => `${childPad}${JSON.stringify(key)}: ${pyLiteral(val, indent + 1)}`
  );
  return `{\n${items.join(',\n')},\n${pad}}`;
}

/** Python (requests) call snippet. */
export function buildPythonSnippet(
  endpoint: SnippetEndpoint,
  options?: SnippetOptions
): string {
  const method = endpoint.method.toLowerCase();
  const bodyArg = isBodyMethod(endpoint.method) ? 'json' : 'params';

  return `import os
import requests

response = requests.${method}(
    "${snippetUrl(endpoint, options)}",
    headers={"x-api-key": os.environ["INNOVATE_API_KEY"]},
    ${bodyArg}=${pyLiteral(buildExampleBody(endpoint), 1)},
)

result = response.json()`;
}

export type SnippetLanguage = 'curl' | 'javascript' | 'python';

/** All three call snippets for the Developer Info tab's language tabs. */
export function buildSnippets(
  endpoint: SnippetEndpoint,
  options?: SnippetOptions
): Record<SnippetLanguage, string> {
  return {
    curl: buildCurlSnippet(endpoint, options),
    javascript: buildJavaScriptSnippet(endpoint, options),
    python: buildPythonSnippet(endpoint, options),
  };
}

/**
 * The name this AI Service appears under in an MCP client's tool list
 * (task 811).
 *
 * **The bare endpoint path, not the service name.** `buildEndpointCatalog` in
 * `innovate-service/src/processes/mcpServer.ts` registers each `mcpExposed`
 * endpoint as `{ name: ep.path }` and dispatches on that same string, so the
 * path IS the tool name. A display derived from the service's `name` would look
 * friendlier and be wrong — the developer would call a tool that does not exist.
 */
export function mcpToolName(endpoint: Pick<Endpoint, 'path'>): string {
  return bareEndpointPath(endpoint.path);
}

/**
 * A ready-to-paste MCP client configuration for the account's endpoint-tools
 * server (task 811).
 *
 * The shape is the `mcpServers` map Claude-family and comparable MCP clients
 * read, keyed by a name the developer sees in their client. The server is
 * ACCOUNT-level — one Streamable HTTP endpoint exposing every `mcpExposed`
 * service the account owns — so the key is the account's slug, not this
 * service's: pasting two of these would otherwise write the same entry twice
 * under different names.
 *
 * The API key is a placeholder on purpose. `/api/mcp/endpoints` authenticates
 * through the same global gate as the rest of `/api` (Bearer JWT or
 * `x-api-key`), and a minted key is shown exactly once — so the snippet names
 * the header and leaves the secret to the reader.
 */
export function mcpClientConfig(
  endpoint: EndpointUrlParts,
  serverUrl: string,
  /**
   * Overrides the account-level key (task 857). The custom-domain address is a
   * PER-SERVICE MCP server rather than the account catalog, so two services
   * pasted from two pages would otherwise write the same `mcpServers` entry
   * twice under one name and the second would silently replace the first.
   */
  clientNameOverride?: string
): string {
  const clientName =
    clientNameOverride ?? (endpoint.accountSlug ? `${endpoint.accountSlug}-innovate` : 'innovate');
  return JSON.stringify(
    {
      mcpServers: {
        [clientName]: {
          url: serverUrl,
          headers: { 'x-api-key': '<YOUR_API_KEY>' },
        },
      },
    },
    null,
    2
  );
}
